import { useCallback, useEffect, useRef } from "react";
import { APP_ACTIVITY_MAX_CLOCK_SKEW_MS } from "@excalidraw-ms/shared";
import { postAppActivity } from "../api/backendClient";
import { AppActivityCollector, isAppUsageInput } from "../utils/appActivityCollector";

interface Options {
  accountId?: string;
  enabled: boolean;
  getToken: () => Promise<string>;
  drawingReady: boolean;
}

export function useAppActivity({ accountId, enabled, getToken, drawingReady }: Options) {
  const collector = useRef<AppActivityCollector | null>(null);
  const identity = useRef(accountId);
  const tokenProvider = useRef(getToken);
  identity.current = accountId;
  tokenProvider.current = getToken;
  const ready = useRef(drawingReady);
  ready.current = drawingReady;
  const readRecorded = useRef(false);
  const foreground = () => document.visibilityState === "visible" && document.hasFocus();

  const record = useCallback(() => {
    if (document.visibilityState === "visible" && document.hasFocus()) collector.current?.record();
  }, []);

  useEffect(() => {
    if (!accountId || !enabled) return;
    let pendingDeliveries = 0;
    const activity = new AppActivityCollector((interval) => {
      if (pendingDeliveries >= 20) {
        console.warn("[appActivity] In-memory delivery limit reached");
        return;
      }
      pendingDeliveries += 1;
      void (async () => {
        if (identity.current !== accountId) return;
        const token = await tokenProvider.current();
        if (identity.current !== accountId) return;
        if (Date.now() - Date.parse(interval.firstOccurredAt) > APP_ACTIVITY_MAX_CLOCK_SKEW_MS) {
          console.warn("[appActivity] Delivery window expired");
          return;
        }
        await postAppActivity(token, [interval]);
      })()
        .catch(() => console.warn("[appActivity] Activity delivery failed"))
        .finally(() => {
          pendingDeliveries -= 1;
        });
    });
    collector.current = activity;
    readRecorded.current = false;
    const recordOpen = () => {
      if (ready.current && !readRecorded.current && foreground()) {
        readRecorded.current = true;
        activity.record();
      }
    };
    const input = (event: Event) => {
      if (foreground() && isAppUsageInput(event)) activity.record();
    };
    const visibility = () => {
      if (!foreground()) activity.flush();
      else recordOpen();
    };
    const flush = () => activity.flush();
    const events = ["pointerdown", "pointermove", "click", "keydown", "input", "wheel", "drop"];
    for (const event of events)
      document.addEventListener(event, input, { capture: true, passive: true });
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("blur", flush);
    window.addEventListener("focus", recordOpen);
    window.addEventListener("pagehide", flush);
    recordOpen();
    return () => {
      for (const event of events) document.removeEventListener(event, input, true);
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("blur", flush);
      window.removeEventListener("focus", recordOpen);
      window.removeEventListener("pagehide", flush);
      collector.current = null;
      if (identity.current === accountId) activity.flush();
      else activity.discard();
    };
  }, [accountId, enabled]);

  useEffect(() => {
    if (drawingReady && !readRecorded.current && collector.current && foreground()) {
      readRecorded.current = true;
      record();
    }
  }, [drawingReady, record]);

  return record;
}
